import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, CircleCheck } from "lucide-react";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";

/**
 * ALTIORA V3 · Adaptive questionnaire — conversational section navigator.
 *
 * Route: /app/dossiers/:dossier_id/questionnaire
 * Doctrine: one section at a time, progressive save (PATCH per section).
 */
export default function DossierQuestionnaire() {
  const { dossier_id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [sections, setSections] = useState([]);
  const [idx, setIdx] = useState(0);
  const [values, setValues] = useState({}); // flat dotted-path → value
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overall, setOverall] = useState(0);

  const loadQ = async () => {
    try {
      const [{ data: d }, { data: q }] = await Promise.all([
        api.get(`/dossiers/${dossier_id}`),
        api.get(`/dossiers/${dossier_id}/questionnaire`),
      ]);
      setDossier(d);
      setSections(q.sections || []);
      setOverall(q.overall_completion || 0);
      // Flatten economic_data into dotted keys for controlled inputs
      const flat = {};
      const walk = (obj, prefix = "") => {
        if (obj == null) return;
        if (typeof obj !== "object" || Array.isArray(obj)) { flat[prefix.slice(0, -1)] = obj; return; }
        Object.entries(obj).forEach(([k, v]) => walk(v, `${prefix}${k}.`));
      };
      walk(d.economic_data || {});
      setValues(flat);
    } catch (err) {
      toast.error("Chargement du questionnaire impossible");
      navigate(`/app/dossiers/${dossier_id}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadQ(); }, [dossier_id]);

  const current = sections[idx];

  const setValue = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

  // Build a nested patch object from flat keys of the current section only.
  const buildSectionPatch = () => {
    if (!current) return {};
    const patch = {};
    current.fields.forEach((f) => {
      const v = values[f.key];
      if (v === undefined) return;
      const parts = f.key.split(".");
      let cur = patch;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = cur[parts[i]] || {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = v;
    });
    return patch;
  };

  const saveAndNext = async (goPrev = false) => {
    setSaving(true);
    try {
      const patch = buildSectionPatch();
      if (Object.keys(patch).length > 0) {
        await api.patch(`/dossiers/${dossier_id}`, { economic_data_patch: patch });
      }
      if (goPrev) setIdx((i) => Math.max(0, i - 1));
      else if (idx < sections.length - 1) setIdx((i) => i + 1);
      else {
        toast.success("Questionnaire complété — retour au dossier");
        navigate(`/app/dossiers/${dossier_id}`);
      }
    } catch (err) {
      toast.error("Enregistrement impossible");
    } finally { setSaving(false); }
  };

  const globalProgress = useMemo(() => {
    if (!sections.length) return 0;
    return Math.round(((idx) / sections.length) * 100);
  }, [idx, sections.length]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7F3]">
        <Loader2 className="h-4 w-4 animate-spin text-[#0B2545]" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937]">
      <header className="border-b border-[#E4E1D8] bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AltioraTreeLogo size={32} />
            <Link to={`/app/dossiers/${dossier_id}`} className="text-xs font-mono uppercase tracking-[0.24em] text-[#5B5B62] hover:text-[#0B2545]" data-testid="q-back-to-dossier">
              <ArrowLeft className="inline h-3.5 w-3.5 mr-1 -mt-0.5" strokeWidth={2} /> Retour au dossier
            </Link>
          </div>
          <div className="text-xs font-mono uppercase tracking-[0.24em] text-[#7A5B14]">
            {idx + 1} / {sections.length}
          </div>
        </div>
        <div className="h-1 bg-[#E4E1D8]">
          <div className="h-1 bg-[#0B2545] transition-all" style={{ width: `${globalProgress}%` }} />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 md:px-8 py-8 md:py-14" data-testid="dossier-questionnaire-page">
        <AnimatePresence mode="wait">
          {current && (
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
                Section {idx + 1} · {dossier.type === "individual" ? "Personne physique" : "Personne morale"}
              </div>
              <h1 className="mt-3 font-display text-3xl md:text-4xl tracking-tight leading-tight text-[#1F2937]">
                {current.name_fr}
              </h1>
              {current.intro_fr && (
                <p className="mt-3 text-[15px] text-[#5B5B62] leading-relaxed max-w-lg">{current.intro_fr}</p>
              )}
              {current.completion === 1 && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#065F46] bg-[#D1FAE5] rounded-full px-2 py-0.5">
                  <CircleCheck className="h-3 w-3" strokeWidth={2} /> Section complète
                </div>
              )}

              <div className="mt-10 space-y-4">
                {current.fields.map((f) => (
                  <QField key={f.key} field={f} value={values[f.key] ?? ""} onChange={(v) => setValue(f.key, v)} />
                ))}
              </div>

              <div className="mt-10 flex items-center justify-between gap-3">
                <button
                  onClick={() => saveAndNext(true)}
                  disabled={idx === 0 || saving}
                  data-testid="q-prev"
                  className="inline-flex items-center gap-2 rounded-md border border-[#E4E1D8] hover:border-[#0B2545] disabled:opacity-40 disabled:cursor-not-allowed bg-white px-4 py-2.5 text-sm text-[#374151] transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Précédent
                </button>
                <button
                  onClick={() => saveAndNext(false)}
                  disabled={saving}
                  data-testid="q-next"
                  className="group inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-70 text-white px-5 py-2.5 text-sm font-medium transition-all"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {idx === sections.length - 1 ? "Terminer" : "Enregistrer & continuer"}
                  {!saving && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {overall > 0 && (
          <div className="mt-14 text-center text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
            Complétude globale du dossier : {Math.round(overall * 100)} %
          </div>
        )}
      </main>
    </div>
  );
}

function QField({ field, value, onChange }) {
  const label = (
    <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">
      {field.label_fr}{field.required && <span className="text-[#DC2626] ml-1">*</span>}
    </div>
  );
  const help = field.help_fr ? <div className="mt-1 text-[11px] text-[#8B8B8F] italic">{field.help_fr}</div> : null;
  const cls = "w-full rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm";
  const testId = `q-field-${field.key.replace(/\./g, "-")}`;

  if (field.type === "textarea") {
    return (
      <label className="block">{label}
        <textarea data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={cls + " resize-y"} />
        {help}
      </label>
    );
  }
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-3 cursor-pointer" data-testid={testId}>
        <div className="relative">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
          <div className="h-5 w-9 rounded-full bg-[#E4E1D8] peer-checked:bg-[#0B2545] transition-colors" />
          <div className="absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full transition-transform peer-checked:translate-x-4 shadow" />
        </div>
        <span className="text-sm text-[#374151]">{field.label_fr}</span>
      </label>
    );
  }
  if (field.type === "select" && field.options) {
    return (
      <label className="block">{label}
        <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}>
          <option value="">— choisir —</option>
          {field.options.map((o) => <option key={o.value} value={o.value}>{o.label_fr}</option>)}
        </select>
        {help}
      </label>
    );
  }
  const inputType = field.type === "currency" || field.type === "percent" || field.type === "number" ? "number"
                  : field.type === "date" ? "date"
                  : field.type === "email" ? "email"
                  : field.type === "phone" ? "tel"
                  : "text";
  return (
    <label className="block">{label}
      <div className="relative">
        <input
          data-testid={testId}
          type={inputType}
          value={value ?? ""}
          onChange={(e) => onChange(inputType === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
          className={cls + (field.unit ? " pr-14" : "")}
          min={field.min}
          max={field.max}
          step={field.type === "percent" ? "0.1" : (field.type === "currency" ? "1" : undefined)}
        />
        {field.unit && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
            {field.unit}
          </div>
        )}
      </div>
      {help}
    </label>
  );
}
